/**
 * PDP Cross-Product Swap (spike) — morphs the current PDP into a different
 * product's PDP in place, without a hard navigation, then applies a staged
 * configuration.
 *
 * Use case: the advisor drawer recommends a frame from a different RMF range.
 * Hard navigating away from the current page is jarring; morphing the main
 * section + history.pushState keeps the drawer overlay state intact and reads
 * as "the advisor changed my page" instead of a context switch.
 *
 * Contract — listen for `pdp:swap-product` with detail:
 *   {
 *     handle: string,                   // destination product handle
 *     sectionId?: string,               // optional override; defaults to 'main'
 *     apply?: { sizeHandle, framingHandle, frameColorHandle, borderOption, styleId },
 *   }
 *
 * If `handle` matches the current PDP, this short-circuits to a direct
 * `pdp:apply-configuration` dispatch (variant/option change only, no morph).
 *
 * Spike scope:
 *   - Single main section morph (RMF PDP composes everything under templates/
 *     product.readymade-frame.json's "main" section, so one fetch covers
 *     gallery, configurator, sticky ATC, room preview, breadcrumbs).
 *   - Components that listen for `section:morph` (cart-drawer, media-gallery)
 *     re-init themselves. PDP-specific components that DON'T yet listen will
 *     surface in the next iteration.
 *   - No prefetch / no view-transition / no scroll preservation tuning yet.
 */

import { sectionRenderer } from '@theme/section-renderer';

const DEFAULT_SECTION_ID = 'main';

function getCurrentPdpHandle() {
  const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function dispatchApply(apply) {
  if (!apply) return;
  document.dispatchEvent(new CustomEvent('pdp:apply-configuration', { detail: apply }));
}

async function handleSwap(event) {
  const detail = event.detail || {};
  const { handle, apply } = detail;
  const sectionId = detail.sectionId || DEFAULT_SECTION_ID;
  if (!handle) {
    console.warn('[pdp-swap] missing handle in detail', detail);
    return;
  }

  // Same product → just apply the config in place; no morph needed.
  if (handle === getCurrentPdpHandle()) {
    dispatchApply(apply);
    return;
  }

  // Build the destination URL. We deliberately keep query params off — the
  // PDP will pick the staged config up via the apply event below, not URL
  // params (and the existing redirect path's `?preset=` etc. were the very
  // bug that motivated this spike).
  const url = new URL(`/products/${encodeURIComponent(handle)}`, window.location.origin);

  document.body.setAttribute('data-pdp-swapping', '1');
  try {
    // renderSection fetches `<url>?section_id=<id>` and morphs the matching
    // <section id="shopify-section-<id>"> in place, then dispatches the
    // theme's `section:morph` event so cart-drawer / media-gallery /
    // anything else listening can re-init.
    await sectionRenderer.renderSection(sectionId, { url, cache: false });

    // Update URL + browser history so back-button works and any subsequent
    // page-state reads (canonical URL, share links) reflect reality.
    history.pushState({ pdpSwap: { from: getCurrentPdpHandle(), to: handle } }, '', url.pathname);

    // Wait one frame so the morphed-in custom elements have had a chance to
    // run their connectedCallback (or for already-mounted instances, their
    // section:morph re-init handlers). Then push the staged configuration.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => dispatchApply(apply));
    });
  } catch (err) {
    console.error('[pdp-swap] morph failed; falling back to navigation:', err);
    // Hard fallback so the user still ends up on the recommended product.
    window.location.href = url.pathname;
  } finally {
    document.body.removeAttribute('data-pdp-swapping');
  }
}

// Restore previous PDP on back/forward — re-fetches the section for the URL
// the browser navigated to. Without this, the user hits Back and gets the
// morphed-in DOM but with the old URL, or vice versa.
window.addEventListener('popstate', async (e) => {
  const currentHandle = getCurrentPdpHandle();
  if (!currentHandle) return;
  // Only handle popstate when we're already on a /products/ URL — leaving
  // the PDP entirely should still be a real navigation.
  try {
    await sectionRenderer.renderSection(DEFAULT_SECTION_ID, {
      url: new URL(window.location.href),
      cache: true,
    });
  } catch (err) {
    console.warn('[pdp-swap] popstate morph failed; reloading:', err);
    window.location.reload();
  }
});

document.addEventListener('pdp:swap-product', handleSwap);
